import React, { useState, useEffect } from "react";

import ManagedTextArea from "input/ManagedTextArea";
import CitationDisplay from "reports/CitationDisplay";

import * as RD from "reports/ReportDef";
import * as RDP from "reports/ReportDataProcessor";

interface ReportDiscoveriesManagerProps {
    discoveries: RD.Discoveries;
    originalDiscoveries: RD.Discoveries;
    citations?: RD.Citations;
    showRestoreButton?: boolean;
    /** The function from the parent's state hook to update the latest value (e.g., the 'update' function) */
    updateDiscoveries: (newValueOrFn: React.SetStateAction<RD.Discoveries>) => void;
}

const ReportDiscoveriesManager: React.FC<ReportDiscoveriesManagerProps> = ({
    discoveries,
    originalDiscoveries,
    citations,
    showRestoreButton,
    updateDiscoveries
}) => {

    const [displayDiscoveries, setDisplayDiscoveries] = useState<RD.Discoveries>([]);

    useEffect(
        () => {
            setDisplayDiscoveries(Array.isArray(discoveries) ? discoveries : []);
        },
        [discoveries]
    );

    const handleAddDiscovery = () => {
        setDisplayDiscoveries(displayDiscoveries.concat({ text: "", citation: undefined }));
        updateDiscoveries(prevDiscoveries => prevDiscoveries.concat({ text: "", citation: undefined }));
    };

    const handleDeleteDiscovery = (index: number) => {
        setDisplayDiscoveries(displayDiscoveries.filter((_, i) => i !== index));
        updateDiscoveries(prevDiscoveries => prevDiscoveries.filter((_, i) => i !== index));
    };

    const handleDeleteAllDiscoveries = () => {
        setDisplayDiscoveries([]);
        updateDiscoveries([]);
    };

    const handleDiscoveryInputChange = (index: number, value: string) => {
        setDisplayDiscoveries(displayDiscoveries.map((discovery, i) => i === index ? { ...discovery, text: value } : discovery))
        updateDiscoveries(prevDiscoveries => prevDiscoveries.map((discovery, i) => i === index ? { ...discovery, text: value } : discovery));
    };

    const handleRevertToInitialDiscoveries = () => {
        setDisplayDiscoveries(originalDiscoveries);
        updateDiscoveries(originalDiscoveries);
    };

    const shouldShowRestore = typeof showRestoreButton === "boolean" ? showRestoreButton : (Array.isArray(discoveries) && discoveries.length > 0);

    return (
        <div id="patient-discoveries" className="w-[80%] mx-auto">
            <div className="px-7 py-1 text-gray-900 text-lg font-semibold leading-7 bg-gray-100 border-gray-300 border-t border-l border-r rounded-t-md shadow-sm mt-4 first:mt-0">
                Patient Discoveries
            </div>
            <div className="px-7 bg-gray-25 border-gray-300 border-l border-r shadow-sm"> {/* Changed bg-gray-25 to bg-gray-50 as -25 is not standard */}
                <div className="py-2 self-stretch text-gray-600 text-sm text-wrap font-normal leading-tight">
                    Outline any breakthroughs the patient has made in understanding and managing their pain.
                </div>
                <div className="py-2 self-stretch text-gray-600 text-sm text-wrap font-normal leading-normal">
                    {
                        displayDiscoveries.length > 0
                            ? (
                                <div className="w-full space-y-4">
                                    {
                                        displayDiscoveries.map((discovery: RD.Discovery, index: number) => {
                                                            const discoveryText = typeof discovery === 'string' ? discovery : discovery?.text ?? "";
                                                            const discoveryCitation = typeof discovery === 'string' ? undefined : discovery?.citation;
                                                            return (
                                            <div key={`discovery-item-${index}`}>
                                                <div className="mb-3">
                                                    <span className="inline-flex gap-x-1 pb-2 justify-between w-full">
                                                        <span className="inline-flex gap-x-1 items-center">
                                                            <p className="text-gray-700 text-sm font-medium leading-tight">Discovery {index + 1}</p>
                                                            <button className="cursor-pointer" type="button" onClick={() => handleDeleteDiscovery(index)}>
                                                                <img className="h-4 w-4 " src={"/icons/trash.svg"} alt="icon"></img>
                                                            </button>
                                                        </span>
                                                        {RDP.ENABLE_CITATIONS_DISPLAY && <CitationDisplay citations={citations} citationIndex={discoveryCitation} />}
                                                    </span>
                                                    <ManagedTextArea
                                                        id={`discovery-${index}`}
                                                        className="w-full p-2 border bg-white border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                        placeholder="Description"
                                                        rows={3}
                                                        // useDirtyState props
                                                        value={discoveryText}
                                                        updateValue={(text: string) => handleDiscoveryInputChange(index, text)}
                                                    />
                                                </div>
                                            </div>
                                                            );
                                        })
                                    }
                                    {
                                        shouldShowRestore && (
                                            <div>
                                                <span className="text-gray-600 flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        className="flex-shrink-0 flex relative justify-center items-center h-7 px-0.5 hover:bg-gray-300 border-gray-300 bg-white rounded-lg shadow border text-sm text-gray-700 font-medium transition-colors duration-150 cursor-pointer"
                                                        onClick={(e) => { e.preventDefault(); handleRevertToInitialDiscoveries(); }}
                                                    >
                                                        <img className="w-6 h-4" src="/icons/flip-backward.svg" alt="icon"/>
                                                    </button>
                                                    <span className="ml-1.5 whitespace-nowrap">
                                                        Restore Unedited Discoveries
                                                    </span>
                                                </span>
                                            </div>
                                        )
                                    }
                                </div>
                            )
                            : (
                                <span className="self-stretch text-warning-600 font-semibold leading-7">&#9888; No Discoveries found in Practice Better data. You can add them manually below.</span>
                            )
                    }
                </div>
                <div className="flex flex-col sm:flex-row self-stretch w-full justify-center items-center gap-3">
                    <button
                        type="button"
                        className="w-fit px-4 py-2.5 bg-white hover:bg-gray-300 rounded-lg shadow border border-gray-300 justify-center items-center gap-1.5 inline-flex cursor-pointer"
                        onClick={e => {e.preventDefault(); handleAddDiscovery();}}
                    >
                        <div className="px-0.5 justify-center gap-x-2 items-center inline-flex">
                            <img className="w-5 h-5 pl-[3.33px] justify-center items-center inline-flex" src="/icons/plus.svg" alt="icon"/>
                            <div className="text-gray-700 text-base font-semibold leading-normal">
                                Add New Discovery
                            </div>
                        </div>
                    </button>
                </div>
            </div>
            <div className="px-7 pb-4 bg-gray-50 border-gray-300 border-l border-r border-b rounded-b-md shadow-sm"/>
        </div>
    );
};

// ---=== Utilities ===--- //

const stringContainsName = (str: string, name: string): boolean => {
    return name.split(" ").some(part => str.toLowerCase().includes(part.toLowerCase()));
};

export const parseSampledDiscoveries = (sampledDiscoveries: string | string[] | any, citations?: number[]): RD.Discoveries => {
    // Support both legacy format (array/string) and new format (object with items and citation)
    let discoveryTexts: string[];
    let citationOverride: number | undefined;

    if (typeof sampledDiscoveries === 'object' && sampledDiscoveries !== null && 'items' in sampledDiscoveries) {
        // New format: { items: string[], citation?: number }
        discoveryTexts = Array.isArray(sampledDiscoveries.items) ? sampledDiscoveries.items : [String(sampledDiscoveries.items)];
        citationOverride = sampledDiscoveries.citation;
    } else if (Array.isArray(sampledDiscoveries)) {
        // Legacy format: plain array
        discoveryTexts = sampledDiscoveries;
    } else {
        // Legacy format: single string
        discoveryTexts = [String(sampledDiscoveries)];
    }

    return discoveryTexts.map((text, index) => ({
        text,
        citation: citationOverride !== undefined ? citationOverride : citations?.[index]
    }));
};

export const discoveriesContainName = (discoveries: RD.Discoveries, name: string) => {
    return discoveries.some((discovery: RD.Discovery) => {
        const text = typeof discovery === 'string' ? discovery : discovery?.text;
        return text && stringContainsName(text, name);
    });
};

export const validateDiscoveries = (discoveries: RD.Discoveries): [RD.Discoveries, string[]] => {
    let validatedDiscoveries: RD.Discoveries = [];
    let errors: string[] = [];

    discoveries.forEach(
        (discovery: RD.Discovery, i: number) => {
            // Handle both old format (string) and new format (object with text property)
            const discoveryText = typeof discovery === 'string' ? discovery : discovery?.text;
            const cleanDiscovery = discoveryText?.trim();

            if (cleanDiscovery) {
                // Normalize to new format
                const normalizedDiscovery = typeof discovery === 'string'
                    ? { text: discovery, citation: undefined }
                    : discovery;
                validatedDiscoveries.push(normalizedDiscovery);
            } else {
                errors.push(`Subjective Outcomes: Incomplete Discovery ${i+1}. Please remove or complete.`);
            }
        }
    );

    return [validatedDiscoveries, errors];
};

export default ReportDiscoveriesManager;